import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { runFreeScan, runAiSearch } from "@/api/explore";
import { ApiError } from "@/api/client";
import { createPipeline } from "@/api/pipeline";
import type { ExploreResult } from "@/types";
import type { ScanHistory, ScanMeta } from "@career-ops-amplify/shared";
import { cn } from "@/cn";
import { useClientPagination } from "@/hooks/usePagination";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { PaginationBar } from "@/components/pagination-bar";
import {
  DISCOVERY_QUERY_KEY,
  useDiscoveryProfile,
  useAddDiscoveredOffer,
  useScanHistory,
  useSaveDiscoveryProfile,
  useClearScanHistory,
  useDismissScanHistory,
} from "@/hooks/useDiscovery";
import { useProfile } from "@/hooks/useApi";
import { useJobs } from "@/hooks/useJobs";
import {
  buildExploreQuickStarts,
  targetRolesAsTitleKeywords,
} from "@/lib/explore-defaults";
import { CompanyScanPicker } from "@/components/explore/company-scan-picker";
import { BoardScanPicker } from "@/components/explore/board-scan-picker";
import { DiscoveryProfileEditor } from "@/components/discovery/discovery-profile-editor";
import {
  parseOfferEnvelopes,
  stripOfferEnvelopes,
} from "@/lib/offer-envelope";
import { normalizeJobUrlClient } from "@/lib/job-csv-mapping";
import { DEFAULT_JOB_BOARDS } from "@career-ops-amplify/shared";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntitySortOption,
} from "@/components/collection";

type ScanMode = "company" | "boards" | "ai";
type ResultsSortField = "relevance" | "company" | "date";
type HistorySortField = "newest" | "oldest" | "title" | "company";

const SCAN_MODES: Array<{ id: ScanMode; label: string; description: string }> = [
  {
    id: "ai",
    label: "Search",
    description:
      "Describe roles in plain language. Web search + AI matching (Serper).",
  },
  {
    id: "company",
    label: "Companies",
    description:
      "Scan employer ATS boards you have enabled (Greenhouse, Ashby, Lever, …).",
  },
  {
    id: "boards",
    label: "Boards",
    description:
      "Scan remote aggregators such as RemoteOK and Hacker News Who’s Hiring.",
  },
];

const SENIORITY_LEVELS = [
  "Intern",
  "Entry",
  "Mid",
  "Senior",
  "Lead",
  "Manager",
  "Director",
  "VP",
  "C-Level",
];

const VERIFICATION_LABELS: Record<string, string> = {
  ats: "Verified ATS",
  board: "Board",
  search_result: "Web result",
  unconfirmed: "Unverified",
};

const RESULTS_SORT_OPTIONS: EntitySortOption<ResultsSortField>[] = [
  { field: "relevance", order: "desc", label: "Relevance" },
  { field: "company", order: "asc", label: "Company A–Z" },
  { field: "date", order: "desc", label: "Date posted" },
];

const HISTORY_SORT_OPTIONS: EntitySortOption<HistorySortField>[] = [
  { field: "newest", order: "desc", label: "Newest" },
  { field: "oldest", order: "asc", label: "Oldest" },
  { field: "title", order: "asc", label: "Title A–Z" },
  { field: "company", order: "asc", label: "Company A–Z" },
];

const RESULTS_VIEW_MODES = ["table", "list", "cards"] as const;
const HISTORY_VIEW_MODES = ["list", "table"] as const;

function resultKey(r: ExploreResult, i: number): string {
  return r.id ?? `${r.url}-${i}`;
}

function csvToList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatScanMeta(meta: ScanMeta): string {
  const parts = [
    `Scanned ${meta.companiesScanned} companies`,
    meta.companiesSkipped > 0
      ? `${meta.companiesSkipped} skipped`
      : null,
    meta.boardsScanned.length > 0
      ? `boards: ${meta.boardsScanned.join(", ")}`
      : null,
    `${meta.newCount} shown`,
    meta.seenCount > 0 ? `${meta.seenCount} already seen` : null,
    (meta.alreadyInInboxCount ?? 0) > 0
      ? `${meta.alreadyInInboxCount} already in inbox`
      : null,
    meta.totalFetched > 0 ? `${meta.totalFetched} fetched before filters` : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

function emptyScanMessage(meta: ScanMeta, keywordsActive: boolean): string {
  if (meta.totalFetched === 0) {
    if (meta.companiesSkipped > 0 && meta.companiesScanned === 0) {
      return "No scannable boards for the selected employers. Enable scan on the employer profile (ATS + board slug) or use Search to hunt the web.";
    }
    return keywordsActive
      ? "No listings matched your Keywords filter. Clear Keywords and try again."
      : "No listings were fetched. The board token may be wrong, or this employer uses an unsupported ATS — try Search instead.";
  }
  if (meta.newCount === 0 && (meta.alreadyInInboxCount ?? 0) > 0) {
    return `${meta.alreadyInInboxCount} listing(s) are already in your Jobs inbox.`;
  }
  if (meta.newCount === 0 && meta.seenCount > 0) {
    return `${meta.seenCount} listing(s) were already in your seen URLs. Enable “Show previously seen” to include them.`;
  }
  if (meta.totalFetched > 0 && meta.newCount === 0) {
    return `${meta.totalFetched} listing(s) were filtered out by Keywords / location. Clear filters to see them.`;
  }
  return "Scan complete — no matches.";
}

function trackZeroResult(reason: string, meta: ScanMeta | null): void {
  try {
    const key = "career-ops:zero-result-events";
    const prev = JSON.parse(localStorage.getItem(key) || "[]") as unknown[];
    const next = [
      ...prev.slice(-49),
      {
        at: new Date().toISOString(),
        reason,
        meta: meta
          ? {
              totalFetched: meta.totalFetched,
              newCount: meta.newCount,
              seenCount: meta.seenCount,
              skipped: meta.companiesSkipped,
            }
          : null,
      },
    ];
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

const SAVED_SEARCHES_KEY = "career-ops:saved-searches";

function loadSavedSearches(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(SAVED_SEARCHES_KEY) || "[]");
    return Array.isArray(raw) ? raw.map(String).filter(Boolean).slice(0, 8) : [];
  } catch {
    return [];
  }
}

function persistSavedSearch(query: string): string[] {
  const q = query.trim();
  if (!q) return loadSavedSearches();
  const next = [q, ...loadSavedSearches().filter((s) => s !== q)].slice(0, 8);
  try {
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

function aiStatusLabel(label: string, count?: number): string {
  switch (label) {
    case "starting":
      return "Starting AI search…";
    case "web-search":
      return "Searching the web…";
    case "analyzing":
      return count != null
        ? `Analyzing ${count} web results…`
        : "Analyzing results…";
    case "ats-boost":
      return "Boosting with verified ATS listings…";
    case "ats-boost-skipped":
      return "Skipping ATS boost (timed out) — continuing with web results…";
    default:
      return label;
  }
}

function formatAiSearchError(err: unknown): string {
  if (err instanceof ApiError) {
    const detail =
      typeof err.body === "object" &&
      err.body !== null &&
      "error" in err.body &&
      String((err.body as { error: unknown }).error).trim()
        ? String((err.body as { error: unknown }).error)
        : err.message;
    if (
      err.status === 500 ||
      err.status === 502 ||
      err.status === 504 ||
      !detail ||
      detail === "Request failed" ||
      detail === `Request failed (${err.status})`
    ) {
      return `AI search failed (HTTP ${err.status}). The server timed out or returned an empty response — please retry.`;
    }
    return detail;
  }
  return err instanceof Error ? err.message : "Search failed.";
}

function verificationColorClass(verification: string): string {
  return verification === "ats" || verification === "search_result"
    ? "bg-emerald-500/10 text-emerald-600"
    : "bg-amber-500/10 text-amber-600";
}

function historyDateMs(entry: ScanHistory): number {
  const raw = entry.firstSeen || entry.createdAt || entry.timestamp;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function historyKey(entry: ScanHistory): string {
  return entry.SK ?? entry.url ?? entry.scanId ?? entry.createdAt;
}

type HistoryRow = ScanHistory & { id: string };

function toHistoryRow(entry: ScanHistory): HistoryRow {
  return { ...entry, id: historyKey(entry) };
}

export function ExplorePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { data: profile } = useDiscoveryProfile();
  const { data: userProfile } = useProfile();
  const saveProfileMutation = useSaveDiscoveryProfile();
  const exploreQuickStarts = useMemo(
    () => buildExploreQuickStarts(userProfile),
    [userProfile],
  );

  const historyCursorPag = useCursorPagination([]);
  const { data: historyData, isLoading: historyLoading } = useScanHistory({
    limit: historyCursorPag.pageSize,
    cursor: historyCursorPag.cursor,
  });
  useSyncCursorPage(
    historyCursorPag.setNextCursor,
    historyData,
    !historyLoading && !!historyData,
  );
  const scanHistory = historyData?.history ?? [];
  const addOfferMutation = useAddDiscoveredOffer();
  const clearHistoryMutation = useClearScanHistory();
  const dismissHistoryMutation = useDismissScanHistory();
  const { data: jobsData } = useJobs({ limit: 500 });

  const [mode, setMode] = useState<ScanMode>("ai");
  const [results, setResults] = useState<ExploreResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [scanMeta, setScanMeta] = useState<ScanMeta | null>(null);
  const [aiReasoning, setAiReasoning] = useState("");
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [savedSearches, setSavedSearches] = useState<string[]>(() =>
    loadSavedSearches(),
  );

  const [keywords, setKeywords] = useState("");
  const [location, setLocation] = useState("");
  const [seniority, setSeniority] = useState<string[]>([]);
  const [requireSeniority, setRequireSeniority] = useState(false);
  /** Boards: location is soft/off by default so EU aggregators are not zeroed. */
  const [boardsHardLocation, setBoardsHardLocation] = useState(false);
  /**
   * inherit = omit from request (use Discovery Portals defaults)
   * custom = send current field values
   * off = send [] (explicitly no filter)
   */
  const [titleFilterMode, setTitleFilterMode] = useState<
    "inherit" | "custom" | "off"
  >("inherit");
  const [locationFilterMode, setLocationFilterMode] = useState<
    "inherit" | "custom" | "off"
  >("inherit");
  const [includeSeen, setIncludeSeen] = useState(false);
  const [scanOptionsOpen, setScanOptionsOpen] = useState(false);
  const [keywordsFromProfile, setKeywordsFromProfile] = useState(false);
  const [keywordsSource, setKeywordsSource] = useState<
    "discovery" | "targetRoles" | null
  >(null);
  const [selectedCompanySlugs, setSelectedCompanySlugs] = useState<string[]>([]);
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [companySearchPrefill, setCompanySearchPrefill] = useState("");
  const boardsInitializedRef = useRef(false);

  const [aiQuery, setAiQuery] = useState("");
  const aiQueryInputRef = useRef<HTMLInputElement>(null);
  const urlParamsAppliedRef = useRef(false);
  const profileFiltersAppliedRef = useRef(false);
  const inboxRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const aiTextBufferRef = useRef("");
  const seenOfferUrlsRef = useRef(new Set<string>());

  const [resultsSearch, setResultsSearch] = useState("");
  const [resultsSort, setResultsSort] = useState<
    EntitySortOption<ResultsSortField>
  >(RESULTS_SORT_OPTIONS[0]);
  const [historySearch, setHistorySearch] = useState("");
  const [historySort, setHistorySort] = useState<
    EntitySortOption<HistorySortField>
  >(HISTORY_SORT_OPTIONS[0]);

  const {
    viewMode: resultsViewMode,
    setViewMode: setResultsViewMode,
    allowedModes: resultsAllowedModes,
  } = useListViewMode({
    storageKey: "viewMode_explore_results",
    defaultMode: "table",
    allowedModes: [...RESULTS_VIEW_MODES],
    paramName: "view",
  });

  const {
    viewMode: historyViewMode,
    setViewMode: setHistoryViewMode,
    allowedModes: historyAllowedModes,
  } = useListViewMode({
    storageKey: "viewMode_explore_history",
    defaultMode: "list",
    allowedModes: [...HISTORY_VIEW_MODES],
    paramName: "historyView",
  });

  const hasBoardSources = selectedBoardIds.length > 0;

  const activeScanOptionCount = useMemo(() => {
    let count = 0;
    if (keywords.trim()) count += 1;
    if (location.trim()) count += 1;
    count += seniority.length;
    if (includeSeen) count += 1;
    return count;
  }, [keywords, location, seniority, includeSeen]);

  const knownJobUrls = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobsData?.jobs ?? []) {
      if (j.url) set.add(normalizeJobUrlClient(j.url));
      if (j.resolvedUrl) set.add(normalizeJobUrlClient(j.resolvedUrl));
      if (j.jobUrl) set.add(normalizeJobUrlClient(j.jobUrl));
    }
    return set;
  }, [jobsData?.jobs]);

  const alreadyInInboxHidden = useMemo(() => {
    return results.filter((r) => {
      if (r.addedJobId) return false;
      return knownJobUrls.has(normalizeJobUrlClient(r.url));
    }).length;
  }, [results, knownJobUrls]);

  const filteredResults = useMemo(() => {
    let list = results.filter((r) => {
      if (r.addedJobId) return true;
      return !knownJobUrls.has(normalizeJobUrlClient(r.url));
    });
    if (resultsSearch.trim()) {
      const q = resultsSearch.toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.company.toLowerCase().includes(q) ||
          (r.location ?? "").toLowerCase().includes(q),
      );
    }
    if (resultsSort.field === "company") {
      const dir = resultsSort.order === "asc" ? 1 : -1;
      list = [...list].sort(
        (a, b) =>
          a.company.localeCompare(b.company, undefined, {
            sensitivity: "base",
          }) * dir,
      );
    } else if (resultsSort.field === "date") {
      const dir = resultsSort.order === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const at = a.postedAt ? new Date(a.postedAt).getTime() : 0;
        const bt = b.postedAt ? new Date(b.postedAt).getTime() : 0;
        return (at - bt) * dir;
      });
    }
    return list;
  }, [results, resultsSearch, resultsSort, knownJobUrls]);

  const filteredHistory = useMemo(() => {
    let list = scanHistory;
    if (historySearch.trim()) {
      const q = historySearch.toLowerCase();
      list = list.filter(
        (entry) =>
          (entry.title ?? "").toLowerCase().includes(q) ||
          (entry.company ?? "").toLowerCase().includes(q) ||
          (entry.portal ?? "").toLowerCase().includes(q) ||
          (entry.url ?? "").toLowerCase().includes(q),
      );
    }
    if (historySort.field === "newest" || historySort.field === "oldest") {
      const dir = historySort.field === "newest" ? -1 : 1;
      list = [...list].sort(
        (a, b) => (historyDateMs(a) - historyDateMs(b)) * dir,
      );
    } else if (historySort.field === "title") {
      const dir = historySort.order === "asc" ? 1 : -1;
      list = [...list].sort(
        (a, b) =>
          (a.title ?? "").localeCompare(b.title ?? "", undefined, {
            sensitivity: "base",
          }) * dir,
      );
    } else if (historySort.field === "company") {
      const dir = historySort.order === "asc" ? 1 : -1;
      list = [...list].sort(
        (a, b) =>
          (a.company ?? "").localeCompare(b.company ?? "", undefined, {
            sensitivity: "base",
          }) * dir,
      );
    }
    return list;
  }, [scanHistory, historySearch, historySort]);

  const resultsPagination = useClientPagination(filteredResults, [
    resultsSearch,
    resultsSort.field,
    resultsSort.order,
  ]);

  const getResultSortValue = useCallback((r: ExploreResult, key: string) => {
    if (key === "title") return r.title;
    if (key === "location") return r.location ?? "";
    return r.company;
  }, []);

  const {
    sortedItems: sortedResultPage,
    sortKey: resultsTableSortKey,
    sortDirection: resultsTableSortDirection,
    onSortChange: onResultsTableSortChange,
  } = useClientTableSort({
    items: resultsPagination.pageItems,
    defaultKey: null,
    getValue: getResultSortValue,
  });

  const getHistorySortValue = useCallback((entry: ScanHistory, key: string) => {
    if (key === "company") return entry.company ?? "";
    if (key === "portal") return entry.portal ?? "";
    if (key === "date") return historyDateMs(entry);
    return entry.title ?? "";
  }, []);

  const {
    sortedItems: sortedHistoryPage,
    sortKey: historyTableSortKey,
    sortDirection: historyTableSortDirection,
    onSortChange: onHistoryTableSortChange,
  } = useClientTableSort({
    items: filteredHistory,
    defaultKey: null,
    getValue: getHistorySortValue,
  });

  function selectMode(next: ScanMode) {
    setMode(next);
    if (next === "boards") {
      setKeywords("");
      setLocation("");
      setSeniority([]);
      setKeywordsFromProfile(false);
      setKeywordsSource(null);
      setTitleFilterMode("off");
      setLocationFilterMode("off");
      setBoardsHardLocation(false);
    }
    if (next === "ai") {
      setScanMeta(null);
    }
  }

  useEffect(() => {
    if (!profile || boardsInitializedRef.current) return;
    const fromProfile = profile.jobBoards ?? [];
    setSelectedBoardIds(
      fromProfile.length > 0 ? fromProfile : [...DEFAULT_JOB_BOARDS],
    );
    boardsInitializedRef.current = true;
  }, [profile]);

  useEffect(() => {
    if (urlParamsAppliedRef.current) return;

    const companyParam = searchParams.get("company")?.trim();
    const slugParam = searchParams.get("slug")?.trim();
    const modeParam = searchParams.get("mode");
    const qParam = searchParams.get("q")?.trim();
    const sourcesParam = searchParams.get("sources");

    if (sourcesParam === "1") {
      setSourcesOpen(true);
    }

    if (slugParam || companyParam) {
      urlParamsAppliedRef.current = true;
      const slug = slugParam ?? companyParam!.toLowerCase().replace(/\s+/g, "-");
      const resolvedMode: ScanMode =
        modeParam === "ai" ? "ai" : modeParam === "boards" ? "boards" : "company";
      selectMode(resolvedMode);

      if (resolvedMode === "ai") {
        setAiQuery(qParam || `Open jobs at ${companyParam ?? slug}`);
        requestAnimationFrame(() => aiQueryInputRef.current?.focus());
      } else if (resolvedMode === "company") {
        setSelectedCompanySlugs([slug]);
        setCompanySearchPrefill(companyParam ?? slug);
      }
      return;
    }

    if (modeParam === "ai" || modeParam === "boards" || modeParam === "company") {
      urlParamsAppliedRef.current = true;
      selectMode(modeParam);
      if (modeParam === "ai" && qParam) {
        setAiQuery(qParam);
        requestAnimationFrame(() => aiQueryInputRef.current?.focus());
      }
    } else if (qParam) {
      urlParamsAppliedRef.current = true;
      selectMode("ai");
      setAiQuery(qParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!profile || profileFiltersAppliedRef.current || mode !== "company") return;
    profileFiltersAppliedRef.current = true;
    if (profile.titleFilter?.positive?.length) {
      setKeywords(profile.titleFilter.positive.join(", "));
      setKeywordsFromProfile(true);
      setKeywordsSource("discovery");
      setTitleFilterMode("custom");
      setScanOptionsOpen(true);
    } else {
      const fromRoles = targetRolesAsTitleKeywords(userProfile?.targetRoles);
      if (fromRoles.length) {
        setKeywords(fromRoles.join(", "));
        setKeywordsFromProfile(true);
        setKeywordsSource("targetRoles");
        setTitleFilterMode("custom");
        setScanOptionsOpen(true);
      }
    }
    if (profile.locationFilter?.allow?.length) {
      setLocation(profile.locationFilter.allow.join(", "));
      setLocationFilterMode("custom");
      setScanOptionsOpen(true);
    } else if (userProfile?.location) {
      const city = userProfile.location.split(/[,(/]/)[0]?.trim();
      if (city) {
        setLocation(city);
        setLocationFilterMode("custom");
        setScanOptionsOpen(true);
      }
    }
  }, [profile, userProfile, mode]);

  function clearScanFilters() {
    setKeywords("");
    setLocation("");
    setSeniority([]);
    setRequireSeniority(false);
    setKeywordsFromProfile(false);
    setKeywordsSource(null);
    setTitleFilterMode("off");
    setLocationFilterMode("off");
  }

  function invalidateScanHistory() {
    void queryClient.invalidateQueries({
      queryKey: [...DISCOVERY_QUERY_KEY, "history"],
    });
  }

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  function toggleSeniority(level: string) {
    setSeniority((prev) =>
      prev.includes(level)
        ? prev.filter((l) => l !== level)
        : [...prev, level],
    );
  }

  function buildScanFilters() {
    const titlePositive = csvToList(keywords);
    const locationAllow = csvToList(location);
    const seniorityBoost: Record<string, number> = {};
    for (const level of seniority) {
      seniorityBoost[level.toLowerCase()] = 5;
    }

    const filters: {
      titlePositive?: string[];
      locationAllow?: string[];
      seniorityBoost?: Record<string, number>;
      seniorityRequire?: string[];
      includeSeen: boolean;
    } = {
      includeSeen,
      seniorityBoost: Object.keys(seniorityBoost).length
        ? seniorityBoost
        : undefined,
      seniorityRequire:
        requireSeniority && seniority.length ? seniority : undefined,
    };

    // inherit = omit key so backend keeps Discovery Portals defaults
    if (titleFilterMode === "custom") {
      filters.titlePositive = titlePositive;
    } else if (titleFilterMode === "off") {
      filters.titlePositive = [];
    }

    if (mode === "boards" && !boardsHardLocation) {
      // Never hard-filter board aggregators by Discovery geo unless opted in.
      filters.locationAllow = [];
    } else if (locationFilterMode === "custom") {
      filters.locationAllow = locationAllow;
    } else if (locationFilterMode === "off") {
      filters.locationAllow = [];
    }

    return filters;
  }

  async function handleCompanyScan() {
    if (selectedCompanySlugs.length === 0) {
      setError("Select at least one company to scan.");
      return;
    }

    setError(null);
    setActionMessage(null);
    setLoading(true);
    setProgress("Scanning ATS job boards…");
    setResults([]);
    setScanMeta(null);
    setAiReasoning("");

    try {
      const res = await runFreeScan({
        ...buildScanFilters(),
        companySlugs: selectedCompanySlugs,
        includeBoards: false,
      });
      setResults(res.results);
      setScanMeta(res.meta ?? null);
      setProgress(
        res.meta
          ? formatScanMeta(res.meta)
          : res.results.length > 0
            ? `Found ${res.results.length} result(s)`
            : "Scan complete — no matches.",
      );
      if (res.results.length === 0 && res.meta) {
        trackZeroResult(emptyScanMessage(res.meta, Boolean(keywords.trim())), res.meta);
      }
      invalidateScanHistory();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Scan failed.");
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleBoardScan() {
    const boardIds = selectedBoardIds;
    if (boardIds.length === 0) {
      setError("Select at least one board aggregator below.");
      return;
    }

    setError(null);
    setActionMessage(null);
    setLoading(true);
    setProgress("Scanning job boards…");
    setResults([]);
    setScanMeta(null);

    try {
      await saveProfileMutation.mutateAsync({ jobBoards: boardIds });
      const res = await runFreeScan({
        ...buildScanFilters(),
        includeBoards: true,
        includeAts: false,
        boardIds,
      });
      setResults(res.results);
      setScanMeta(res.meta ?? null);
      setProgress(
        res.meta
          ? formatScanMeta(res.meta)
          : `Found ${res.results.length} result(s)`,
      );
      if (res.results.length === 0 && res.meta) {
        trackZeroResult(emptyScanMessage(res.meta, Boolean(keywords.trim())), res.meta);
      }
      invalidateScanHistory();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Board scan failed.");
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleAiSearch() {
    if (!aiQuery.trim()) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setError(null);
    setActionMessage(null);
    setLoading(true);
    setProgress("Starting web search…");
    setResults([]);
    setScanMeta(null);
    setAiReasoning("");
    aiTextBufferRef.current = "";
    seenOfferUrlsRef.current = new Set();
    setSavedSearches(persistSavedSearch(aiQuery));

    try {
      await runAiSearch(
        aiQuery.trim(),
        (event) => {
          if (event.type === "text") {
            aiTextBufferRef.current += event.text;
            const offers = parseOfferEnvelopes(aiTextBufferRef.current);
            const newResults: ExploreResult[] = [];

            for (const offer of offers) {
              if (seenOfferUrlsRef.current.has(offer.url)) continue;
              seenOfferUrlsRef.current.add(offer.url);
              newResults.push({
                portal: offer.source ?? "ai-search",
                url: offer.url,
                title: offer.title,
                company: offer.company,
                location: offer.location,
                postedAt: offer.postedHint,
                why: offer.why,
                source: offer.source ?? "ai-search",
                verification: offer.verification,
                id: offer.url,
              });
            }

            if (newResults.length > 0) {
              setResults((prev) => [...prev, ...newResults]);
            }
            setAiReasoning(stripOfferEnvelopes(aiTextBufferRef.current));
          } else if (event.type === "status") {
            const count =
              "count" in event && typeof event.count === "number"
                ? event.count
                : undefined;
            setProgress(aiStatusLabel(event.label, count));
          } else if (event.type === "error") {
            setError(event.msg);
          } else if (event.type === "done") {
            setProgress(
              `Search complete${event.tokens ? ` (${event.tokens} tokens)` : ""}`,
            );
          }
        },
        controller.signal,
      );
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") return;
      setError(formatAiSearchError(err));
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }

  function handleCancelAi() {
    abortRef.current?.abort();
    setLoading(false);
    setProgress("Search cancelled.");
  }

  function resolveResultIndex(result: ExploreResult): number {
    return results.findIndex(
      (r) =>
        (result.id != null && r.id === result.id) || r.url === result.url,
    );
  }

  async function handleAddToInbox(result: ExploreResult, index: number) {
    setActionMessage(null);
    try {
      const source =
        result.source === "ai-search" || result.portal === "ai-search"
          ? "ai-search"
          : "scan";

      const res = await addOfferMutation.mutateAsync({
        offers: [
          {
            url: result.url,
            company: result.company,
            role: result.title,
            location: result.location,
            compensation: result.compensation,
            source,
          },
        ],
        source,
      });

      const job = res.added[0];
      if (job) {
        setResults((prev) =>
          prev.map((r, i) =>
            i === index ? { ...r, addedJobId: job.id ?? job.jobId } : r,
          ),
        );
        setActionMessage(
          `Added ${result.title} at ${result.company} to your jobs inbox.`,
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add to inbox.");
    }
  }

  async function handleStartPipeline(jobId: string) {
    try {
      const res = await createPipeline(jobId);
      const pipelineId = res.pipeline?.id ?? res.pipeline?.pipelineId;
      if (pipelineId) {
        navigate(`/pipeline/${pipelineId}`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create pipeline.");
    }
  }

  const renderResultActions = (r: ExploreResult) => (
    <div
      className="flex shrink-0 flex-wrap gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      <a
        href={r.url}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-md border border-border px-3 py-1.5 text-center text-xs font-medium text-muted-foreground transition hover:bg-muted/20"
      >
        View
      </a>
      {r.addedJobId ? (
        <>
          <Link
            to={`/jobs/${r.addedJobId}`}
            className="rounded-md border border-brand/30 bg-brand/10 px-3 py-1.5 text-center text-xs font-medium text-brand"
          >
            In inbox
          </Link>
          <button
            type="button"
            onClick={() => handleStartPipeline(r.addedJobId!)}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground"
          >
            Pipeline
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => {
            const index = resolveResultIndex(r);
            if (index >= 0) void handleAddToInbox(r, index);
          }}
          disabled={addOfferMutation.isPending}
          className="rounded-md bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand transition hover:bg-brand/20 disabled:opacity-50"
        >
          + Inbox
        </button>
      )}
    </div>
  );

  const resultColumns = useMemo((): Column<ExploreResult>[] => {
    return [
      {
        key: "company",
        header: "Company",
        sortable: true,
        render: (r) => (
          <span className="font-medium text-foreground">{r.company}</span>
        ),
      },
      {
        key: "title",
        header: "Title",
        sortable: true,
        render: (r) => (
          <div className="min-w-0">
            <div className="text-foreground">{r.title}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span className="text-brand">{r.portal}</span>
              {r.verification ? (
                <span className={cn("rounded px-1.5 py-0.5", verificationColorClass(r.verification))}>
                  {VERIFICATION_LABELS[r.verification] ?? r.verification}
                </span>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: "location",
        header: "Location",
        sortable: true,
        render: (r) => (
          <span className="text-muted-foreground">
            {r.location ?? "Location not specified"}
          </span>
        ),
      },
      {
        key: "why",
        header: "Why",
        render: (r) =>
          r.why ? (
            <span className="line-clamp-2 text-xs text-muted-foreground">
              {r.why}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        key: "actions",
        header: "Actions",
        className: "text-right",
        render: (r) => renderResultActions(r),
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- actions close over mutation state
  }, [addOfferMutation.isPending, results]);

  const dismissHistoryEntry = (entry: ScanHistory) => {
    const scanId = entry.scanId;
    if (!scanId) return;
    dismissHistoryMutation.mutate(scanId);
  };

  const historyColumns = useMemo((): Column<HistoryRow>[] => {
    return [
      {
        key: "title",
        header: "Title",
        sortable: true,
        render: (entry) => (
          <span className="font-medium text-foreground">
            {entry.title ?? "Unknown"}
          </span>
        ),
      },
      {
        key: "company",
        header: "Company",
        sortable: true,
        render: (entry) => (
          <span className="text-muted-foreground">{entry.company ?? "—"}</span>
        ),
      },
      {
        key: "portal",
        header: "Portal",
        sortable: true,
        render: (entry) => (
          <span className="text-muted-foreground">{entry.portal ?? "—"}</span>
        ),
      },
      {
        key: "date",
        header: "Seen",
        sortable: true,
        render: (entry) => {
          const ms = historyDateMs(entry);
          return (
            <span className="text-muted-foreground">
              {ms ? new Date(ms).toLocaleDateString() : "—"}
            </span>
          );
        },
      },
      {
        key: "actions",
        header: "",
        className: "text-right",
        render: (entry) => (
          <div
            className="flex items-center justify-end gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {entry.url ? (
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand hover:underline"
              >
                View
              </a>
            ) : null}
            {entry.scanId ? (
              <button
                type="button"
                onClick={() => dismissHistoryEntry(entry)}
                disabled={dismissHistoryMutation.isPending}
                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                Dismiss
              </button>
            ) : null}
          </div>
        ),
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dismiss closes over mutation
  }, [dismissHistoryMutation.isPending]);

  const keywordChips = useMemo(
    () => csvToList(keywords),
    [keywords],
  );

  const [historySinceMs] = useState(() => {
    try {
      const raw = localStorage.getItem("career-ops:last-find-jobs-visit");
      const prev = raw ? Number(raw) : 0;
      localStorage.setItem(
        "career-ops:last-find-jobs-visit",
        String(Date.now()),
      );
      return Number.isFinite(prev) ? prev : 0;
    } catch {
      return 0;
    }
  });

  const newSinceLastVisit = useMemo(() => {
    if (!historySinceMs) return 0;
    return scanHistory.filter((e) => historyDateMs(e) > historySinceMs).length;
  }, [scanHistory, historySinceMs]);

  return (
    <>
      <header className="mb-8 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Find Jobs</h1>
          <p className="text-muted-foreground">
            Search the web, scan employer boards, or pull from remote aggregators.
            Results are temporary — add to Inbox to keep a role.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSourcesOpen((o) => !o)}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/20"
        >
          {sourcesOpen ? "Hide my sources" : "My sources"}
        </button>
      </header>

      {sourcesOpen && (
        <div className="mb-6 rounded-lg border border-border bg-card p-4">
          <p className="mb-3 text-xs text-muted-foreground">
            Default title/location preferences and tracked employers. Board selection lives under the Boards tab.
            Advanced catalog toggles:{" "}
            <Link to="/settings/providers" className="text-brand hover:underline">
              Settings → Providers
            </Link>
            .
          </p>
          <DiscoveryProfileEditor
            compact
            onSaved={() => setActionMessage("Sources saved.")}
          />
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        {SCAN_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => selectMode(m.id)}
            className={cn(
              "rounded-full border px-4 py-2 text-left text-sm transition",
              mode === m.id
                ? "border-brand bg-brand/10 text-brand"
                : "border-border bg-card text-muted-foreground hover:border-muted hover:text-foreground",
            )}
          >
            <span className="font-medium">{m.label}</span>
            <span className="mt-0.5 block text-[11px] font-normal opacity-80">
              {m.description}
            </span>
          </button>
        ))}
      </div>

      {(mode === "company" || mode === "boards") && (
        <div className="mb-6 rounded-lg border border-border bg-card">
          <button
            type="button"
            onClick={() => setScanOptionsOpen((open) => !open)}
            className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition hover:bg-muted/10"
            aria-expanded={scanOptionsOpen}
          >
            <span className="flex items-center gap-2 text-sm font-medium text-foreground">
              Scan options
              {activeScanOptionCount > 0 ? (
                <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-medium text-brand">
                  {activeScanOptionCount}
                </span>
              ) : null}
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition",
                scanOptionsOpen && "rotate-180",
              )}
            />
          </button>
          {scanOptionsOpen ? (
            <div className="border-t border-border px-4 py-3">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="keywords"
                    className="text-xs font-medium text-foreground"
                  >
                    Keywords
                    {mode === "boards" && (
                      <span className="ml-1 font-normal text-muted-foreground">
                        (optional)
                      </span>
                    )}
                  </label>
                  <input
                    id="keywords"
                    type="text"
                    placeholder={
                      mode === "boards"
                        ? "Leave empty for all listings"
                        : "e.g. engineer, staff"
                    }
                    value={keywords}
                    onChange={(e) => {
                      const next = e.target.value;
                      setKeywords(next);
                      if (!next.trim()) {
                        setKeywordsFromProfile(false);
                        setKeywordsSource(null);
                        setTitleFilterMode("off");
                      } else {
                        setTitleFilterMode("custom");
                      }
                    }}
                    className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  {keywordChips.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] text-amber-700 dark:text-amber-300">
                        {keywordsFromProfile
                          ? keywordsSource === "targetRoles"
                            ? "Filtering titles (from Profile target roles):"
                            : "Filtering titles (from your Discovery sources):"
                          : "Filtering titles:"}
                      </span>
                      {keywordChips.map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          onClick={() => {
                            const next = keywordChips.filter((c) => c !== chip);
                            setKeywords(next.join(", "));
                            if (next.length === 0) {
                              setKeywordsFromProfile(false);
                              setKeywordsSource(null);
                              setTitleFilterMode("off");
                            } else {
                              setTitleFilterMode("custom");
                            }
                          }}
                          className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-800 dark:text-amber-100"
                        >
                          {chip} ×
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setKeywords("");
                          setKeywordsFromProfile(false);
                          setKeywordsSource(null);
                          setTitleFilterMode("off");
                        }}
                        className="text-[11px] text-brand hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="location"
                    className="text-xs font-medium text-foreground"
                  >
                    Location
                  </label>
                  <input
                    id="location"
                    type="text"
                    placeholder="e.g. Remote, US"
                    value={location}
                    onChange={(e) => {
                      const next = e.target.value;
                      setLocation(next);
                      setLocationFilterMode(next.trim() ? "custom" : "off");
                    }}
                    className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  {mode === "boards" ? (
                    <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={boardsHardLocation}
                        onChange={(e) => setBoardsHardLocation(e.target.checked)}
                        className="rounded border-border"
                      />
                      Hard-filter board results by location
                      <span className="text-muted-foreground/80">
                        (off by default — aggregators are often EU-skewed)
                      </span>
                    </label>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Seniority
                  </label>
                  <p className="text-[11px] text-muted-foreground">
                    Chips boost sort order. Turn on “Only show selected” to hard-filter.
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {SENIORITY_LEVELS.map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => toggleSeniority(level)}
                        className={cn(
                          "rounded-md border px-2 py-0.5 text-[11px] transition",
                          seniority.includes(level)
                            ? "border-brand bg-brand/10 text-brand"
                            : "border-border text-muted-foreground hover:border-muted",
                        )}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                  <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={requireSeniority}
                      onChange={(e) => setRequireSeniority(e.target.checked)}
                      disabled={seniority.length === 0}
                      className="rounded border-border"
                    />
                    Only show selected seniority
                  </label>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={includeSeen}
                    onChange={(e) => setIncludeSeen(e.target.checked)}
                    className="rounded border-border"
                  />
                  Show previously seen
                </label>
                <button
                  type="button"
                  onClick={clearScanFilters}
                  className="text-xs text-brand hover:underline"
                >
                  Clear filters
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {mode === "company" && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Select employers
            </h2>
            <Link
              to="/companies"
              className="text-xs text-brand hover:underline"
            >
              Browse employers
            </Link>
          </div>
          <CompanyScanPicker
            selectedSlugs={selectedCompanySlugs}
            onChange={setSelectedCompanySlugs}
            initialSearch={companySearchPrefill}
            onSearchWeb={(name) => {
              selectMode("ai");
              setAiQuery(`Open jobs at ${name}`);
              requestAnimationFrame(() => aiQueryInputRef.current?.focus());
            }}
          />
          <button
            type="button"
            onClick={handleCompanyScan}
            disabled={loading || selectedCompanySlugs.length === 0}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand px-6 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Scanning…" : "Scan selected"}
          </button>
        </div>
      )}

      {mode === "boards" && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Board aggregators
            </h2>
            <Link
              to="/settings/providers"
              className="text-xs text-brand hover:underline"
            >
              Provider catalog
            </Link>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">
            Choose which aggregators to scan. Selection is saved when you run a scan.
          </p>
          <BoardScanPicker
            selectedIds={selectedBoardIds}
            onChange={setSelectedBoardIds}
          />
          <button
            type="button"
            onClick={handleBoardScan}
            disabled={loading || !hasBoardSources}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand px-6 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Scanning…" : "Scan boards"}
          </button>
        </div>
      )}

      {mode === "ai" && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <label htmlFor="ai-query" className="mb-2 block text-sm font-medium text-foreground">
            What are you looking for?
          </label>
          <div className="flex gap-2">
            <input
              ref={aiQueryInputRef}
              id="ai-query"
              type="text"
              placeholder='e.g. "Senior backend role at Stripe or Notion, remote US"'
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAiSearch();
              }}
              className="flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <button
              type="button"
              onClick={handleAiSearch}
              disabled={loading || !aiQuery.trim()}
              className="rounded-md bg-brand px-5 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Searching…" : "Search"}
            </button>
            {loading && (
              <button
                type="button"
                onClick={handleCancelAi}
                className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground"
              >
                Cancel
              </button>
            )}
          </div>
          {savedSearches.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {savedSearches.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setAiQuery(s);
                    requestAnimationFrame(() => aiQueryInputRef.current?.focus());
                  }}
                  className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:border-brand hover:text-brand"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Configure Serper in{" "}
            <Link to="/settings/ai" className="text-brand hover:underline">
              Settings → AI
            </Link>
            . Results are grounded in real web search.
          </p>
        </div>
      )}

      {progress && (
        <div className="mb-6 flex items-center gap-2 rounded-md border border-brand/20 bg-brand/5 px-4 py-2 text-sm text-brand">
          {loading && (
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-brand/30 border-t-brand" />
          )}
          {progress}
        </div>
      )}

      {scanMeta && scanMeta.skipReasons.length > 0 && (
        <div className="mb-6 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-800 dark:text-amber-200">
          <p className="font-medium">Skipped companies</p>
          <ul className="mt-1 list-inside list-disc">
            {scanMeta.skipReasons.map((s) => (
              <li key={s.slug}>
                {s.name}: {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {aiReasoning && mode === "ai" && (
        <div className="mb-6 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          {aiReasoning}
        </div>
      )}

      {actionMessage && (
        <div className="mb-6 rounded-md border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
          {actionMessage}
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {results.length > 0 && (
        <div ref={inboxRef}>
          <h2 className="mb-1 text-lg font-semibold text-foreground">
            Results
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({filteredResults.length}
              {filteredResults.length !== results.length
                ? ` of ${results.length}`
                : ""}{" "}
              found)
            </span>
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Results are temporary. Add to Inbox to keep a role.
            {alreadyInInboxHidden > 0
              ? ` · ${alreadyInInboxHidden} already in inbox (hidden)`
              : ""}
            {(scanMeta?.alreadyInInboxCount ?? 0) > 0 && alreadyInInboxHidden === 0
              ? ` · ${scanMeta?.alreadyInInboxCount} already in inbox (hidden)`
              : ""}
          </p>
          <div className="mb-4">
            <CollectionCommandBar
              searchQuery={resultsSearch}
              onSearchChange={setResultsSearch}
              searchPlaceholder="Search title, company, or location…"
              sortOption={resultsSort}
              onSortChange={(s) =>
                setResultsSort(s as EntitySortOption<ResultsSortField>)
              }
              sortOptions={RESULTS_SORT_OPTIONS}
              viewMode={resultsViewMode}
              onViewModeChange={setResultsViewMode}
              allowedModes={resultsAllowedModes}
              onClear={() => setResultsSearch("")}
            />
          </div>
          {filteredResults.length === 0 ? (
            <div className="rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-sm text-muted-foreground">
                {alreadyInInboxHidden > 0 && !resultsSearch.trim()
                  ? `${alreadyInInboxHidden} listing(s) are already in your Jobs inbox.`
                  : "No results match your search."}
              </p>
            </div>
          ) : (
            <>
              <EntityListLayout
                viewMode={resultsViewMode}
                table={
                  <DataTable
                    data={sortedResultPage}
                    columns={resultColumns}
                    sortKey={resultsTableSortKey}
                    sortDirection={resultsTableSortDirection}
                    onSortChange={onResultsTableSortChange}
                    getRowId={(r, i) => resultKey(r, i)}
                  />
                }
                list={
                  <div className="space-y-2">
                    {resultsPagination.pageItems.map((r, i) => (
                      <EntityCompactItem
                        key={resultKey(r, i)}
                        id={resultKey(r, i)}
                        title={r.title}
                        description={`${r.company} · ${r.location ?? "Location not specified"}`}
                        badges={[
                          {
                            label: r.portal,
                            colorClass: "bg-brand/10 text-brand",
                          },
                          ...(r.verification
                            ? [
                                {
                                  label:
                                    VERIFICATION_LABELS[r.verification] ??
                                    r.verification,
                                  colorClass: verificationColorClass(
                                    r.verification,
                                  ),
                                },
                              ]
                            : []),
                        ]}
                        metadata={
                          r.why
                            ? [{ value: r.why, className: "line-clamp-2" }]
                            : undefined
                        }
                        actions={renderResultActions(r)}
                      />
                    ))}
                  </div>
                }
                cards={
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {resultsPagination.pageItems.map((r, i) => (
                      <EntityCard
                        key={resultKey(r, i)}
                        id={resultKey(r, i)}
                        title={r.title}
                        description={r.why}
                        primaryBadge={{
                          label: r.portal,
                          colorClass: "bg-brand/10 text-brand",
                        }}
                        secondaryBadge={
                          r.verification
                            ? {
                                label:
                                  VERIFICATION_LABELS[r.verification] ??
                                  r.verification,
                                colorClass: verificationColorClass(
                                  r.verification,
                                ),
                              }
                            : undefined
                        }
                        metadata={[
                          { value: r.company },
                          {
                            value: r.location ?? "Location not specified",
                          },
                        ]}
                        actions={renderResultActions(r)}
                      />
                    ))}
                  </div>
                }
              />
              <PaginationBar
                page={resultsPagination.page}
                pageSize={resultsPagination.pageSize}
                totalItems={filteredResults.length}
                totalPages={resultsPagination.totalPages}
                showingFrom={resultsPagination.showingFrom}
                showingTo={resultsPagination.showingTo}
                onPageChange={resultsPagination.setPage}
                onPageSizeChange={resultsPagination.setPageSize}
              />
            </>
          )}
        </div>
      )}

      {(scanHistory.length > 0 || historyCursorPag.page > 1) && (
        <details className="mt-10 rounded-lg border border-border bg-card">
          <summary className="cursor-pointer list-none px-4 py-3 text-sm text-muted-foreground marker:content-none [&::-webkit-details-marker]:hidden">
            <span className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className="font-semibold uppercase tracking-wide text-foreground">
                  Seen URLs (dedup)
                </span>
                <span className="ml-2 font-normal normal-case">
                  · {historyData?.total ?? scanHistory.length}
                  {historyCursorPag.hasMore ? "+" : ""} logged
                  {newSinceLastVisit > 0
                    ? ` · ${newSinceLastVisit} new since last visit`
                    : ""}
                </span>
              </span>
              <span className="text-xs text-brand">Show / hide</span>
            </span>
          </summary>
          <div className="space-y-4 border-t border-border px-4 py-4">
            <p className="text-xs text-muted-foreground">
              Dedup log from Company/Board scans — not your Jobs inbox. Dismiss hides
              a row; Clear removes all so listings can appear as new again.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (
                    !window.confirm(
                      "Clear all seen URLs? Future scans may show these listings again as new.",
                    )
                  ) {
                    return;
                  }
                  clearHistoryMutation.mutate(undefined, {
                    onSuccess: (res) => {
                      setActionMessage(
                        res.message ?? `Cleared ${res.deleted} seen URL(s).`,
                      );
                      historyCursorPag.goFirst();
                    },
                    onError: (err: unknown) => {
                      setError(
                        err instanceof Error
                          ? err.message
                          : "Failed to clear seen URLs.",
                      );
                    },
                  });
                }}
                disabled={clearHistoryMutation.isPending}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/20 disabled:opacity-50"
              >
                {clearHistoryMutation.isPending
                  ? "Clearing…"
                  : "Clear seen history"}
              </button>
            </div>
            <CollectionCommandBar
              searchQuery={historySearch}
              onSearchChange={setHistorySearch}
              searchPlaceholder="Search seen URLs…"
              sortOption={historySort}
              onSortChange={(s) =>
                setHistorySort(s as EntitySortOption<HistorySortField>)
              }
              sortOptions={HISTORY_SORT_OPTIONS}
              viewMode={historyViewMode}
              onViewModeChange={setHistoryViewMode}
              allowedModes={historyAllowedModes}
              onClear={() => setHistorySearch("")}
            />
            {filteredHistory.length === 0 ? (
              <div className="rounded-lg border border-border bg-bg p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {historySearch.trim()
                    ? "No history matches your search on this page."
                    : "No seen URLs yet."}
                </p>
              </div>
            ) : (
              <EntityListLayout
                viewMode={historyViewMode}
                table={
                  <DataTable
                    data={sortedHistoryPage.map(toHistoryRow)}
                    columns={historyColumns}
                    density="compact"
                    sortKey={historyTableSortKey}
                    sortDirection={historyTableSortDirection}
                    onSortChange={onHistoryTableSortChange}
                  />
                }
                list={
                  <div className="space-y-2">
                    {filteredHistory.map((entry) => (
                      <EntityCompactItem
                        key={historyKey(entry)}
                        id={historyKey(entry)}
                        title={entry.title ?? "Unknown"}
                        description={`${entry.company ?? "—"} · ${entry.portal ?? "—"}`}
                        actions={
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {entry.url ? (
                              <a
                                href={entry.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-brand hover:underline"
                              >
                                View
                              </a>
                            ) : null}
                            {entry.scanId ? (
                              <button
                                type="button"
                                onClick={() => dismissHistoryEntry(entry)}
                                disabled={dismissHistoryMutation.isPending}
                                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                              >
                                Dismiss
                              </button>
                            ) : null}
                          </div>
                        }
                      />
                    ))}
                  </div>
                }
              />
            )}
            {(scanHistory.length > 0 || historyCursorPag.page > 1) && (
              <PaginationBar
                mode="cursor"
                page={historyCursorPag.page}
                pageSize={historyCursorPag.pageSize}
                itemCount={scanHistory.length}
                showingFrom={
                  scanHistory.length === 0 ? 0 : historyCursorPag.showingFrom
                }
                showingTo={historyCursorPag.showingTo(scanHistory.length)}
                hasMore={historyCursorPag.hasMore}
                canPrev={historyCursorPag.canPrev}
                onFirst={historyCursorPag.goFirst}
                onPrev={historyCursorPag.goPrev}
                onNext={historyCursorPag.goNext}
                onPageSizeChange={historyCursorPag.setPageSize}
              />
            )}
          </div>
        </details>
      )}

      {!loading &&
        results.length === 0 &&
        !error &&
        scanMeta &&
        (mode === "company" || mode === "boards") && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-8 text-center">
            <p className="text-sm text-amber-900 dark:text-amber-100">
              {emptyScanMessage(scanMeta, Boolean(keywords.trim()))}
            </p>
          </div>
        )}

      {!loading && results.length === 0 && !error && !progress && !scanMeta && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="mb-4 text-sm text-muted-foreground">
            {mode === "company"
              ? "Select employers with a public ATS board, or enable scan from an employer profile."
              : mode === "boards"
                ? "Pick aggregators below and scan for remote listings."
                : "Describe a role to search the web, or try a quick start:"}
          </p>
          {mode === "ai" && (
            <div className="flex flex-wrap justify-center gap-2">
              {exploreQuickStarts.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => {
                    setAiQuery(q);
                    requestAnimationFrame(() => aiQueryInputRef.current?.focus());
                  }}
                  className="rounded-md bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand/20"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {mode === "company" && (
            <p className="text-xs text-muted-foreground">
              Enable scan on an employer profile, then select it under My enabled.
            </p>
          )}
        </div>
      )}
    </>
  );
}
